import React, { useEffect, useMemo, useState } from "react";
import "./projectSetup.css";
import { fetchProfile, fetchRoles } from "../../api/authApi";
import { fetchCustomers } from "../../api/customersApi";
import { getExchangeRate } from "../../api/nbgCurrencyApi";
import { createProject, fetchProjects, updateProject } from "../../api/projectsApi";
import {
  fetchBuildingTypes,
  fetchProjectStructure,
  fetchStructureTemplates,
  saveProjectStructure,
  saveStructureTemplate,
  saveSystemTemplate,
} from "../../api/projectStructureApi";
import { indexTypes } from "../../structure/buildingTypes";
import { setSameStructure, syncTrees } from "../../structure/siteOps";
import { structureDiff } from "../../structure/structureDiff";
import {
  DEFAULT_MARKUPS,
  defaultSite,
  freeze,
  isSingleBuilding,
  nodesToTemplate,
  renumberFrozen,
  validateTree,
  workbookSheets,
} from "../../structure/structureRules";
import { clearDraft, loadDraft, saveDraft } from "../../structure/setupDraft";
import { confirmDialog } from "../../utils/confirmDialog";
import { timeAgo } from "../../utils/timeAgo";
import MembersTab from "../tabs/MembersTab";
import BudgetingStep from "./BudgetingStep";
import GeneralInfoStep from "./GeneralInfoStep";
import PlotStep from "./PlotStep";
import StructureEditor from "./StructureEditor";

const HISTORY_LIMIT = 100;
const DEFAULT_DOC_TITLE = "საორიენტაციო ღირებულება";

const NEW_STEPS = [
  { key: "general", title: "ზოგადი ინფორმაცია", sub: "შენობის ტიპი განსაზღვრავს, რომელი შაბლონით დაიწყება სტრუქტურა. დოკუმენტის ველები TOTAL-ის სათაურში ჩაიწერება." },
  { key: "plot", title: "ნაკვეთი და შენობები", sub: "რა დგას ნაკვეთზე. შემდეგ ნაბიჯზე აეწყობა ხარჯთაღრიცხვის სტრუქტურა." },
  { key: "structure", title: "სტრუქტურა", sub: "ხარჯთაღრიცხვის სტრუქტურა თითო ობიექტზე: დისციპლინები, ქვე-დისციპლინები და სისტემები (▣ — Excel-ში ბლოკი)." },
];
const EDIT_TABS = [
  { key: "general", title: "ზოგადი ინფორმაცია", sub: "პროექტი, შენობის ტიპი, დოკუმენტი (TOTAL-ის სათაური) და ვალუტა." },
  { key: "plot", title: "ნაკვეთი და შენობები", sub: "რა დგას ნაკვეთზე; თითო ობიექტს თავისი TOTAL აქვს." },
  { key: "structure", title: "სტრუქტურა", sub: "ხარჯთაღრიცხვის სტრუქტურა თითო ობიექტზე." },
  { key: "budget", title: "ბიუჯეტირება", sub: "როგორ გამოიყურება ხარჯთაღრიცხვა Excel-ში და რა ემატება პირდაპირ ხარჯს. მარჯვნივ — შედეგი." },
  { key: "users", title: "მომხმარებლები", sub: "ვის აქვს წვდომა ამ პროექტზე." },
];

const today = () => new Date().toISOString().slice(0, 10);
const defaultSettings = () => ({ detail: "detailed", english: true, frozen: false, nextNo: {}, order: "object", inUnit: false, markups: DEFAULT_MARKUPS });

const emptyForm = () => ({
  name: "",
  location: "",
  customerId: "",
  date: today(),
  stage: "design",
  buildingType: "",
  docTitle: DEFAULT_DOC_TITLE,
  preparedBy: "",
  currency: "GEL",
  currency2: "USD",
  rate: "",
});

const formFromProject = (p, settings) => ({
  name: p.name || "",
  location: p.location || "",
  customerId: p.customer_id || "",
  date: p.date || today(),
  stage: settings?.document?.stage || "design",
  buildingType: /^\d{2}\.\d{2}/.test(p.building_type || "") ? p.building_type : "",
  docTitle: settings?.document?.title || DEFAULT_DOC_TITLE,
  preparedBy: settings?.document?.preparedBy || "",
  currency: p.real_currency || "GEL",
  currency2: p.additional_currency && p.additional_currency !== p.real_currency ? p.additional_currency : "",
  rate: p.course ? String(p.course) : "",
});

/** In frozen mode every row needs a number: give rows that have none the next free one. */
function numberFrozen(trees, nextNo) {
  const outTrees = {};
  const outNext = { ...nextNo };
  Object.entries(trees).forEach(([id, tree]) => {
    const t = JSON.parse(JSON.stringify(tree));
    outNext[id] = renumberFrozen(t, nextNo[id] || {});
    outTrees[id] = t;
  });
  return { trees: outTrees, nextNo: outNext };
}

export default function ProjectSetup({ token, mode, projectId, onDone, onCancel }) {
  const isNew = mode === "new";
  const steps = isNew ? NEW_STEPS : EDIT_TABS;
  const [stepKey, setStepKey] = useState(steps[0].key);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [template, setTemplate] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [typeIndex, setTypeIndex] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [project, setProject] = useState(null);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [doc, setDoc] = useState({ site: null, trees: {}, settings: defaultSettings() });
  const [baseline, setBaseline] = useState(null);
  const [revision, setRevision] = useState(0);
  const [formBaseline, setFormBaseline] = useState(null);
  const [structureExists, setStructureExists] = useState(false);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [treesTouched, setTreesTouched] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);

  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [review, setReview] = useState(null);
  const [draft, setDraft] = useState(null); // unfinished project from an earlier session, offered to the user

  // ---------- load ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tpl, types, custs, profile, roles] = await Promise.all([
          fetchStructureTemplates(token),
          fetchBuildingTypes(token),
          fetchCustomers(token).catch(() => []),
          fetchProfile(token).catch(() => null),
          fetchRoles(token).catch(() => []),
        ]);
        if (cancelled) return;
        setTemplate(tpl.system);
        if (!tpl.system) setMessage({ kind: "warn", text: "ნაგულისხმევი შაბლონი ბაზაში ჯერ არ არის — სტრუქტურა ცარიელიდან დაიწყება. ადმინისტრატორმა უნდა ჩატვირთოს (api/import_structure_data.py)." });
        if (!(types.items || []).length) setMessage({ kind: "warn", text: "შენობების კლასიფიკატორი ბაზაში ცარიელია — დაამატეთ მონაცემთა ბაზებში („შენობის ტიპები“)." });
        setSavedTemplates(tpl.saved || []);
        setTypeIndex(indexTypes(types.items || []));
        setCustomers(Array.isArray(custs) ? custs : []);
        setIsSuperuser(!!profile?.is_superuser);

        if (isNew) {
          const site = defaultSite();
          setDoc({ site, trees: syncTrees(site, {}, tpl.system), settings: defaultSettings() });
          setDraft(loadDraft());
        } else {
          const [projects, structure] = await Promise.all([fetchProjects(token), fetchProjectStructure(token, projectId)]);
          if (cancelled) return;
          const p = (projects || []).find((x) => String(x.id) === String(projectId));
          if (!p) throw new Error("პროექტი ვერ მოიძებნა ან წვდომა არ გაქვთ");
          setProject(p);
          const role = (roles || []).find((r) => String(r.project_id) === String(projectId))?.role;
          setCanManageUsers(!!profile?.is_superuser || role === "admin");
          const settings = { ...defaultSettings(), ...(structure.settings || {}) };
          const site = structure.exists ? structure.site : defaultSite({ plotName: p.location ? `${p.location} — ნაკვეთი` : "ნაკვეთი" });
          const trees = structure.exists ? structure.trees : syncTrees(site, {}, tpl.system);
          setForm(formFromProject(p, settings));
          setFormBaseline(formFromProject(p, settings));
          setDoc({ site, trees, settings });
          setBaseline(structure.exists ? { site, trees, frozen: !!settings.frozen } : { site: { ents: [] }, trees: {}, frozen: false });
          setRevision(structure.revision || 0);
          setStructureExists(!!structure.exists);
          setTreesTouched(true);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "ჩატვირთვა ვერ მოხერხდა");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, isNew, projectId]);

  // ---------- edits with undo ----------
  const commit = (next) => {
    setPast((p) => [...p.slice(-(HISTORY_LIMIT - 1)), doc]);
    setFuture([]);
    setDoc(next);
  };
  const undo = () => {
    if (!past.length) return;
    setFuture((f) => [doc, ...f]);
    setDoc(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    setPast((p) => [...p, doc]);
    setDoc(future[0]);
    setFuture((f) => f.slice(1));
  };

  const withNumbers = (trees, settings) => {
    if (!settings.frozen) return { trees, settings };
    const r = numberFrozen(trees, settings.nextNo || {});
    return { trees: r.trees, settings: { ...settings, nextNo: r.nextNo } };
  };

  const onSiteChange = (site, trees) => {
    const reset = isNew && !treesTouched && isSingleBuilding(doc.site) !== isSingleBuilding(site);
    const synced = syncTrees(site, trees, template, { reset });
    commit({ ...doc, site, ...withNumbers(synced, doc.settings) });
  };
  const onTreeChange = (objectId, tree) => {
    setTreesTouched(true);
    commit({ ...doc, ...withNumbers({ ...doc.trees, [objectId]: tree }, doc.settings) });
  };
  const onSameStructure = async (on) => {
    const confirmed =
      !on ||
      (await confirmDialog("ყველა შენობა პირველის სტრუქტურას გამოიყენებს; დანარჩენი შენობების საკუთარი სტრუქტურა წაიშლება. გავაგრძელოთ?", {
        confirmLabel: "გაგრძელება",
        cancelLabel: "გაუქმება",
        danger: false,
      }));
    if (!confirmed) return;
    const r = setSameStructure(doc.site, doc.trees, on);
    commit({ ...doc, site: r.site, ...withNumbers(r.trees, doc.settings) });
  };
  const onSettings = (settings) => commit({ ...doc, settings });
  const onFreeze = async (on) => {
    if (on) {
      const trees = {};
      const nextNo = {};
      Object.entries(doc.trees).forEach(([id, tree]) => {
        const f = freeze(tree);
        trees[id] = f.tree;
        nextNo[id] = f.next;
      });
      commit({ ...doc, trees, settings: { ...doc.settings, frozen: true, nextNo } });
    } else if (await confirmDialog("ნომრები ისევ ავტომატური გახდება: წაშლილი ხაზების შემდეგ ნომრები შეიკვრება და ფურცლების სახელები შეიძლება შეიცვალოს. გავაგრძელოთ?", { confirmLabel: "გაგრძელება", cancelLabel: "გაუქმება", danger: false })) {
      commit({ ...doc, settings: { ...doc.settings, frozen: false } });
    }
  };
  const onSaveTemplate = async (body) => {
    try {
      const saved = await saveStructureTemplate(token, body);
      setSavedTemplates((list) => [...list.filter((t) => t.id !== saved.id), saved]);
    } catch (err) {
      setMessage({ kind: "err", text: err.message });
    }
  };
  const onSaveSystemTemplate = async (tree) => {
    try {
      setTemplate(await saveSystemTemplate(token, { tree: nodesToTemplate(tree, template?.tree || []) }));
      return true;
    } catch (err) {
      setMessage({ kind: "err", text: err.message });
      return false;
    }
  };
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const refreshRate = async () => {
    if (!form.currency2) return;
    try {
      setField("rate", (await getExchangeRate(form.currency, form.currency2)).toFixed(4));
    } catch (err) {
      setMessage({ kind: "err", text: "კურსი ვერ მოიძებნა" });
    }
  };
  useEffect(() => {
    if (isNew && form.currency2 && !form.rate) refreshRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, form.currency, form.currency2]);

  // ---------- derived ----------
  const workbook = useMemo(() => (doc.site ? workbookSheets(doc.site, doc.trees, { frozen: doc.settings.frozen }) : []), [doc]);
  const sheetCount = workbook.reduce((s, o) => s + o.sheets.length + 1, 0);
  const problems = useMemo(() => {
    if (!doc.site) return [];
    const out = [];
    workbook.forEach((o) => o.sheets.filter((s) => s.err).forEach((s) => out.push(`${s.tab}: ${s.err}`)));
    Object.values(doc.trees).forEach((t) => validateTree(t).forEach((e) => out.push(e.error)));
    return out;
  }, [doc, workbook]);
  const nameMissing = !form.name.trim();
  const typeMissing = !form.buildingType;
  const generalMissing = nameMissing || !form.customerId || typeMissing;
  const missingLabel = nameMissing ? "დასახელება" : !form.customerId ? "დამკვეთი" : "შენობის ტიპი";
  const formChanged = !isNew && !!formBaseline && JSON.stringify(form) !== JSON.stringify(formBaseline);
  const structureChanged = past.length > 0 || !structureExists;
  const canEditGeneral = isNew || canManageUsers; // updating a project needs project admin
  const dirty = past.length > 0 || formChanged || (isNew && !!form.name);

  // ---------- draft (new project only) ----------
  useEffect(() => {
    if (!isNew || loading || draft || !dirty) return undefined;
    const t = setTimeout(() => saveDraft({ form, doc, stepKey, treesTouched }), 800);
    return () => clearTimeout(t);
  }, [isNew, loading, draft, dirty, form, doc, stepKey, treesTouched]);

  const resumeDraft = () => {
    setForm({ ...emptyForm(), ...draft.form });
    setDoc({ ...draft.doc, settings: { ...defaultSettings(), ...(draft.doc.settings || {}) } });
    setTreesTouched(!!draft.treesTouched);
    if (steps.some((s) => s.key === draft.stepKey)) setStepKey(draft.stepKey);
    setPast([]);
    setFuture([]);
    setDraft(null);
  };
  const discardDraft = () => {
    clearDraft();
    setDraft(null);
  };

  // ---------- save ----------
  const settingsToSave = () => ({
    ...doc.settings,
    document: { title: form.docTitle, preparedBy: form.preparedBy, stage: form.stage },
  });

  const finish = (pid, created) => onDone?.({ projectId: pid, created });

  const create = async () => {
    setShowErrors(true);
    if (generalMissing) {
      setStepKey("general");
      setMessage({ kind: "err", text: `${missingLabel} აკლია — ნაბიჯი 1` });
      return;
    }
    if (problems.length) {
      setStepKey("structure");
      setMessage({ kind: "err", text: problems[0] });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      let pid = createdProjectId;
      if (!pid) {
        const p = await createProject(token, {
          name: form.name.trim(),
          location: form.location.trim(),
          customer_id: Number(form.customerId),
          date: form.date || today(),
          language: "geo",
          real_currency: form.currency,
          additional_currency: form.currency2 || form.currency,
          course: Number(String(form.rate).replace(",", ".")) || 1,
          building_type: form.buildingType,
          area: Math.round(Number(String(doc.site.area || "").replace(",", ".")) || 0),
          status: "new",
          description: form.docTitle || "",
          members: [],
        });
        pid = p.id;
        setCreatedProjectId(pid);
      }
      await saveProjectStructure(token, pid, { site: doc.site, trees: doc.trees, settings: settingsToSave(), revision: 0 });
      clearDraft();
      finish(pid, true);
    } catch (err) {
      setMessage({
        kind: "err",
        text: createdProjectId || err.errors?.length ? `სტრუქტურა ვერ შეინახა: ${err.errors?.[0]?.error || err.message}` : err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const openReview = () => {
    if (formChanged && generalMissing) {
      setShowErrors(true);
      setStepKey("general");
      setMessage({ kind: "err", text: `${missingLabel} სავალდებულოა` });
      return;
    }
    if (problems.length) {
      setStepKey("structure");
      setMessage({ kind: "err", text: problems[0] });
      return;
    }
    setReview(structureDiff(baseline, { site: doc.site, trees: doc.trees, frozen: !!doc.settings.frozen }));
  };

  const saveEdit = async () => {
    setSaving(true);
    setMessage(null);
    try {
      if (formChanged && canEditGeneral) {
        await updateProject(token, projectId, {
          name: form.name.trim(),
          location: form.location.trim(),
          customer_id: Number(form.customerId),
          date: form.date,
          real_currency: form.currency,
          additional_currency: form.currency2 || form.currency,
          course: Number(String(form.rate).replace(",", ".")) || 1,
          building_type: form.buildingType,
          description: form.docTitle || "",
        });
        setFormBaseline(form);
      }
      if (structureChanged || formChanged) {
        const saved = await saveProjectStructure(token, projectId, { site: doc.site, trees: doc.trees, settings: settingsToSave(), revision });
        setRevision(saved.revision);
      }
      setReview(null);
      finish(projectId, false);
    } catch (err) {
      setReview(null);
      setMessage({
        kind: "err",
        text:
          err.status === 409
            ? `სტრუქტურა სხვამ შეცვალა (ვერსია ${err.revision}). დახურეთ ფანჯარა და გახსენით ხელახლა — თქვენი ცვლილებები არ შეინახა.`
            : err.errors?.[0]?.error || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (dirty && !(await confirmDialog("შეუნახავი ცვლილებები დაიკარგება. დავხუროთ?", { confirmLabel: "დახურვა", cancelLabel: "დარჩენა" }))) return;
    if (isNew) clearDraft(); // closed on purpose; only a crash or a forced close keeps the draft
    onCancel?.();
  };

  // ---------- render ----------
  const stepIndex = steps.findIndex((s) => s.key === stepKey);
  const step = steps[stepIndex];

  if (loading || loadError) {
    return (
      <div className="psetup" data-no-translate="true">
        <div className="dhead">
          <b>{isNew ? "ახალი პროექტი" : "პროექტის პარამეტრები"}</b>
          <button type="button" className="ic" onClick={() => onCancel?.()} aria-label="დახურვა">
            ✕
          </button>
        </div>
        <div className="loading">{loadError ? <div className="msg err">{loadError}</div> : "იტვირთება…"}</div>
      </div>
    );
  }

  return (
    <div className="psetup">
      <div className="dhead">
        <span>
          <b>{isNew ? "ახალი პროექტი" : "პროექტის პარამეტრები"}</b>
          <span className="sub" data-no-translate="true">
            {isNew ? form.name : `· ${project?.name || ""}`}
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {doc.settings.frozen && <span className="snote">🔒 ნომრები დაფიქსირებულია</span>}
          <button type="button" className="ic" onClick={cancel} aria-label="დახურვა">
            ✕
          </button>
        </span>
      </div>

      <div className="wbody">
        <nav className="wside">
          {steps.map((s, i) => (
            <button key={s.key} type="button" className={`wstep${s.key === stepKey ? " on" : ""}${isNew && i < stepIndex ? " done" : ""}`} onClick={() => setStepKey(s.key)}>
              <span className="mark">{isNew && i < stepIndex ? "✓" : i + 1}</span>
              {s.title}
            </button>
          ))}
        </nav>

        <main className="wmain">
          <div className="whead">
            <h3>{step.title}</h3>
            <p>{step.sub}</p>
          </div>
          {isNew && draft && (
            <div style={{ padding: "8px 18px 0" }} data-no-translate="true">
              <div className="msg warn" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 180 }}>
                  ნაპოვნია დაუმთავრებელი პროექტი{draft.form?.name ? ` „${draft.form.name}“` : ""} · {timeAgo(draft.savedAt)}
                </span>
                <button type="button" className="btn pri" onClick={resumeDraft}>
                  გაგრძელება
                </button>
                <button type="button" className="btn" onClick={discardDraft}>
                  თავიდან დაწყება
                </button>
              </div>
            </div>
          )}
          {message && (
            <div style={{ padding: "8px 18px 0" }}>
              <div className={`msg ${message.kind}`}>{message.text}</div>
            </div>
          )}
          {stepKey === "structure" ? (
            <StructureEditor
              site={doc.site}
              trees={doc.trees}
              frozen={!!doc.settings.frozen}
              template={template}
              savedTemplates={savedTemplates}
              isSuperuser={isSuperuser}
              onTreeChange={onTreeChange}
              onSameStructure={onSameStructure}
              onSaveTemplate={onSaveTemplate}
              onSaveSystemTemplate={onSaveSystemTemplate}
              onUndo={undo}
              onRedo={redo}
              canUndo={past.length > 0}
              canRedo={future.length > 0}
            />
          ) : (
            <div className="wscroll" data-no-translate={stepKey === "users" ? undefined : "true"}>
              {stepKey === "general" && !canEditGeneral && (
                <div style={{ padding: "8px 18px 0" }}>
                  <div className="msg warn">ამ მონაცემებს მხოლოდ პროექტის ადმინისტრატორი ცვლის.</div>
                </div>
              )}
              {stepKey === "general" && (
                <fieldset disabled={!canEditGeneral} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <GeneralInfoStep
                  form={form}
                  setField={setField}
                  customers={customers}
                  typeIndex={typeIndex}
                  nameMissing={nameMissing}
                  showErrors={showErrors}
                  docNumber={project?.doc_number}
                  onRefreshRate={refreshRate}
                />
                </fieldset>
              )}
              {stepKey === "plot" && <PlotStep site={doc.site} trees={doc.trees} onChange={onSiteChange} />}
              {stepKey === "budget" && <BudgetingStep settings={doc.settings} onChange={onSettings} objectCount={workbook.length} onFreeze={onFreeze} />}
              {stepKey === "users" && (
                <div style={{ padding: "12px 18px" }}>
                  <MembersTab token={token} projectId={projectId} canManage={canManageUsers} />
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      <div className="dfoot">
        <span className={`recap${isNew && stepKey === "structure" && generalMissing ? " bad" : ""}`}>
          {isNew ? `ნაბიჯი ${stepIndex + 1} / ${steps.length}` : ""}
          {isNew && stepKey === "structure" && generalMissing
            ? ` · ${missingLabel} აკლია — ნაბიჯი 1`
            : ` · შეიქმნება: ${workbook.length} ობიექტი · ${sheetCount} ფურცელი${problems.length ? ` · ${problems.length} შესასწორებელი` : ""}`}
        </span>
        <span className="acts">
          <button type="button" className="btn" onClick={cancel}>
            გაუქმება
          </button>
          {isNew && (
            <button type="button" className="btn" disabled={stepIndex === 0} onClick={() => setStepKey(steps[stepIndex - 1].key)}>
              ‹ უკან
            </button>
          )}
          {isNew && stepIndex < steps.length - 1 && (
            <button type="button" className="btn pri" onClick={() => setStepKey(steps[stepIndex + 1].key)}>
              შემდეგი ›
            </button>
          )}
          {isNew && stepIndex === steps.length - 1 && (
            <button type="button" className="btn pri" disabled={saving} onClick={create}>
              {saving ? "იქმნება…" : "პროექტის შექმნა"}
            </button>
          )}
          {!isNew && (
            <button type="button" className="btn pri" disabled={saving || !(structureChanged || formChanged)} onClick={openReview}>
              შენახვა
            </button>
          )}
        </span>
      </div>

      {review && (
        <div className="rvveil">
          <div className="rvcard" data-no-translate="true">
            <h4>შენახვამდე: რა შეიცვლება Excel-ში</h4>
            <div className="sub">
              {review.length ? `${review.length} ცვლილება ფურცლებში` : "ფურცლები არ იცვლება"}
              {formChanged ? " · პროექტის ზოგადი ინფორმაცია განახლდება (TOTAL-ის სათაური — Excel-ის შემდეგ განახლებისას)" : ""}
            </div>
            <div className="rvlist">
              {review.map((c, i) => (
                <div key={i} className={c.kind === "add" ? "add" : c.kind === "remove" ? "del" : "ren"}>
                  {c.kind === "add" && `+ ახალი ფურცელი ${c.tab}`}
                  {c.kind === "remove" && `− წაიშლება ${c.tab}`}
                  {c.kind === "rename" && `↻ ${c.from} → ${c.tab}`}
                </div>
              ))}
            </div>
            <div className="rvfoot">
              <button type="button" className="btn" onClick={() => setReview(null)}>
                ‹ უკან, რედაქტირება
              </button>
              <button type="button" className="btn pri" disabled={saving} onClick={saveEdit}>
                {saving ? "ინახება…" : "შენახვა"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
